import { useToast } from '@kk/ui/components/Notifications/Toast';
import addDays from 'date-fns/addDays';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  type CreateNewAgreementRequest,
  useCategories,
  useContactList,
  useCreateNewAgreement,
  useCustomerList,
  useFrameworkShorts,
} from '@/api/hooks/useNewAgreement';
import {
  Fields,
  getSelectValue,
  isPassengerCarCategory,
  type NewAgreementFormValues,
  toNewAgreementApplicationPayload,
} from './NewAgreementPage/NewAgreement/newAgreementForm.model';

type SummaryItem = {
  id: string;
  label: string;
  value: string;
};

type SummaryFieldProps = Pick<SummaryItem, 'label' | 'value'>;

type ContactLike =
  | {
      name?: string;
      phoneNumber?: string;
      email?: string;
    }
  | null
  | undefined;

type LesseeLike =
  | {
      name?: string;
      address?: string;
      postalCode?: string;
      city?: string;
    }
  | null
  | undefined;

type EanNumberLike =
  | {
      eanNumber?: string;
    }
  | null
  | undefined;

type SelectOptionLike = {
  label: string;
  value: string;
};

type NewAgreementNavigationState = Partial<NewAgreementFormValues> & {
  contractNo?: string | null;
  leasingPaymentAmount?: number | null;
};

function SummaryField({ label, value }: SummaryFieldProps) {
  return (
    <div>
      <dt className="text-sm font-normal text-neutral-100">{label}</dt>
      <dd className="whitespace-pre-line text-[15px] text-neutral-200">
        {value || '-'}
      </dd>
    </div>
  );
}

function parseNumber(value: string | null | undefined) {
  if (!value?.trim()) return undefined;

  const normalized = value.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);

  return Number.isNaN(parsed) ? undefined : parsed;
}

function formatDkk(value: number | undefined | null) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return `${new Intl.NumberFormat('da-DK').format(Math.round(value))} DKK ekskl. moms`;
}

function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function formatDate(value: unknown) {
  const date = parseDateValue(value);
  return date ? new Intl.DateTimeFormat('da-DK').format(date) : '-';
}

function addMonths(date: Date, months: number) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function getPaymentFrequencyMultiplier(value: unknown) {
  switch (getSelectValue(value)) {
    case 'M3':
      return 3;
    case 'M6':
      return 6;
    default:
      return 1;
  }
}

function getPaymentFrequencyPeriodLabel(value: unknown, count: number) {
  switch (getSelectValue(value)) {
    case 'M3':
      return count === 1 ? 'kvartal' : 'kvartaler';
    case 'M6':
      return 'halvår';
    default:
      return count === 1 ? 'måned' : 'måneder';
  }
}

function formatLeasingPeriod(
  leasingPeriodValue: unknown,
  paymentFrequencyValue: unknown,
) {
  const period = Number(getSelectValue(leasingPeriodValue));
  if (!Number.isInteger(period) || period <= 0) return '-';

  const multiplier = getPaymentFrequencyMultiplier(paymentFrequencyValue);
  const leasingPeriodMonths = period * multiplier;
  const periodLabel = getPaymentFrequencyPeriodLabel(
    paymentFrequencyValue,
    period,
  );

  if (multiplier === 1) {
    return `${period} ${periodLabel}`;
  }

  return `${period} ${periodLabel} (${leasingPeriodMonths} måneder)`;
}

function formatValue(value: unknown) {
  const selected = getSelectValue(value);
  return selected || '-';
}

function toIntegerOrZero(value: unknown) {
  const parsedValue = Number.parseInt(getSelectValue(value).trim(), 10);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function formatSelectedOptionValue(
  value: unknown,
  options: SelectOptionLike[],
) {
  const selected = getSelectValue(value);
  if (!selected) return '-';

  return (
    options.find(
      (option) => option.value && String(option.value) === String(selected),
    )?.label ?? selected
  );
}

const leasingPaymentLabels: Record<string, string> = {
  fast: 'Fast',
  variabel: 'Variabel',
};

const paymentFrequencyLabels: Record<string, string> = {
  M1: 'Månedligt',
  M3: 'Kvartalsvist',
  M6: 'Halvårligt',
};

function formatMappedValue(value: unknown, labels: Record<string, string>) {
  const selected = getSelectValue(value);
  return selected ? (labels[selected] ?? selected) : '-';
}

function formatFileName(files: File[] | File | null | undefined) {
  const normalizedFiles = Array.isArray(files) ? files : files ? [files] : [];

  return normalizedFiles.map((file) => file.name).join('\n') || '-';
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('load', () => {
      const result = reader.result;

      if (typeof result !== 'string') {
        reject(new Error(`Could not read file "${file.name}".`));
        return;
      }

      resolve(result.split(',')[1] ?? '');
    });

    reader.addEventListener('error', () => {
      reject(reader.error ?? new Error(`Could not read file "${file.name}".`));
    });

    reader.readAsDataURL(file);
  });
}

async function serializeFiles(files: File[] | null | undefined) {
  return Promise.all(
    asArray(files).map(async (file) => ({
      filename: file.name,
      base64: await readFileAsBase64(file),
    })),
  );
}

function createNewAgreementRequest(
  _navigationState: NewAgreementNavigationState,
  pBlobPayload: unknown,
): CreateNewAgreementRequest {
  const pContractNo = '';
  const pBlob = JSON.stringify(pBlobPayload, null, 2);

  return {
    pContractNo,
    pBlob,
  };
}

function formatBoolean(
  value: boolean | undefined,
  trueLabel = 'Ja',
  falseLabel = 'Nej',
) {
  if (typeof value !== 'boolean') return '-';
  return value ? trueLabel : falseLabel;
}

function formatContact(value: ContactLike) {
  if (!value) return '-';

  return (
    [value.name, value.phoneNumber, value.email].filter(Boolean).join('\n') ||
    '-'
  );
}

function formatContactNameAndEmail(value: ContactLike) {
  if (!value) return '-';

  return [value.name, value.email].filter(Boolean).join(', ') || '-';
}

function formatLessee(value: LesseeLike) {
  if (!value) return '-';

  return (
    [
      value.name,
      value.address,
      [value.postalCode, value.city].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join('\n') || '-'
  );
}

function formatEanNumber(value: EanNumberLike) {
  return value?.eanNumber?.trim() || '-';
}

function formatSelectedOrNewContact(
  selectedValue: unknown,
  newValue: ContactLike,
  options: SelectOptionLike[],
) {
  const formattedNewValue = formatContact(newValue);
  return formattedNewValue !== '-'
    ? formattedNewValue
    : formatSelectedOptionValue(selectedValue, options);
}

function formatAuthorizedSignatories(
  selectedValue: unknown,
  newValue: ContactLike | ContactLike[],
  options: SelectOptionLike[],
) {
  const selected = getSelectValue(selectedValue);

  const selectedLabels = selected
    ? selected
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean)
        .map(
          (value) =>
            options.find((option) => String(option.value) === String(value))
              ?.label ?? value,
        )
    : [];

  const newLabels = asArray(
    Array.isArray(newValue) ? newValue : newValue ? [newValue] : [],
  )
    .map(formatContactNameAndEmail)
    .filter((value) => value !== '-');

  const labels = [...selectedLabels, ...newLabels].filter(
    (value, index, values) =>
      values.findIndex(
        (candidate) =>
          candidate.trim().toLowerCase() === value.trim().toLowerCase(),
      ) === index,
  );

  return labels.join('\n') || '-';
}

function formatSelectedOrNewLessee(
  selectedValue: unknown,
  newValue: LesseeLike,
  options: SelectOptionLike[],
) {
  const formattedNewValue = formatLessee(newValue);
  return formattedNewValue !== '-'
    ? formattedNewValue
    : formatSelectedOptionValue(selectedValue, options);
}

function formatSelectedOrNewEanNumber(
  selectedValue: unknown,
  newValue: EanNumberLike,
) {
  const formattedNewValue = formatEanNumber(newValue);
  return formattedNewValue !== '-'
    ? formattedNewValue
    : formatValue(selectedValue);
}

export default function SummaryNewAgreement() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const navigationState = location.state as NewAgreementNavigationState | null;
  const formValues = navigationState;

  const { data: customers, isLoading: isFetchingCustomers } = useCustomerList();
  const { data: contacts, isLoading: isFetchingContacts } = useContactList(
    formValues?.[Fields.lessee],
  );
  const { mutateAsync: createNewAgreement, isPending: isCreatingNewAgreement } =
    useCreateNewAgreement();
  const { data: frameworkShorts } = useFrameworkShorts();
  const { data: categories = [] } = useCategories();

  const frameworkAgreementOptions = useMemo<SelectOptionLike[]>(() => {
    if (!Array.isArray(frameworkShorts)) return [];

    return frameworkShorts
      .filter((model) => {
        const code = model.code?.trim();
        return typeof code === 'string' && code.length > 0;
      })
      .map((model) => {
        const code = model.code?.trim() ?? '';

        return {
          label: `${code}${model.description ? ` "${model.description}"` : ''}`,
          value: code,
        };
      });
  }, [frameworkShorts]);

  const leasingCategoryOptions = useMemo<SelectOptionLike[]>(() => {
    if (!Array.isArray(categories)) return [];

    return categories
      .filter(
        (category) =>
          typeof category.code === 'string' && category.code.trim().length > 0,
      )
      .map((category) => {
        const code = category.code?.trim() ?? '';
        const description = category.description?.trim();

        return {
          value: code,
          label: description || code,
        };
      });
  }, [categories]);

  const lesseeOptions = useMemo<SelectOptionLike[]>(
    () =>
      isFetchingCustomers
        ? []
        : asArray(customers).map((customer) => ({
            label: customer.name2 ?? customer.name ?? customer.code ?? '',
            value: String(customer.code ?? ''),
          })),
    [customers, isFetchingCustomers],
  );

  const contactOptions = useMemo<SelectOptionLike[]>(
    () =>
      isFetchingContacts
        ? []
        : asArray(contacts).map((contact) => ({
            label: [contact.name, contact.email].filter(Boolean).join(', '),
            value: String(contact.code ?? ''),
          })),
    [contacts, isFetchingContacts],
  );

  const endDate = useMemo(() => {
    if (!formValues) return '-';

    const firstDueDate = formValues[Fields.firstDueDate];
    const leasingPeriod = Number(
      getSelectValue(formValues[Fields.leasingPeriod]),
    );

    const parsedFirstDueDate = parseDateValue(firstDueDate);

    if (
      !parsedFirstDueDate ||
      !Number.isInteger(leasingPeriod) ||
      leasingPeriod <= 0
    ) {
      return '-';
    }

    return formatDate(
      addDays(
        addMonths(
          parsedFirstDueDate,
          leasingPeriod *
            getPaymentFrequencyMultiplier(formValues[Fields.paymentFrequency]),
        ),
        -1,
      ),
    );
  }, [formValues]);

  const isPassengerCar = isPassengerCarCategory(
    formValues?.[Fields.leasingCategory],
  );

  const leftColumn: SummaryItem[] = [
    {
      id: 'fundingAmount',
      label: t('common.fundingAmount'),
      value: formatDkk(parseNumber(formValues?.[Fields.fundingAmount])),
    },
    {
      id: 'firstDueDate',
      label: t('common.firstDueDate'),
      value: formatDate(formValues?.[Fields.firstDueDate]),
    },
    {
      id: 'frameworkAgreement',
      label: t('common.frameworkAgreement'),
      value: formatSelectedOptionValue(
        formValues?.[Fields.frameworkAgreement],
        frameworkAgreementOptions,
      ),
    },
    {
      id: 'leasingCategory',
      label: t('select.equipmentType'),
      value: formatSelectedOptionValue(
        formValues?.[Fields.leasingCategory],
        leasingCategoryOptions,
      ),
    },
    {
      id: 'residualValue',
      label: t('common.residualValue'),
      value: formatDkk(parseNumber(formValues?.[Fields.residualValue])),
    },
    {
      id: 'initialPayment',
      label: t('common.initialPayment'),
      value: formatDkk(parseNumber(formValues?.[Fields.fixedFirstPayment])),
    },
    {
      id: 'calculatedLeasingPayment',
      label: t('common.leasingPayment'),
      value:
        navigationState?.leasingPaymentAmount != null
          ? [
              formatDkk(navigationState.leasingPaymentAmount),
              t('common.disclaimer.description'),
            ].join('\n')
          : '-',
    },
    {
      id: 'leasingPeriod',
      label: t('common.leasingPeriod'),
      value: formatLeasingPeriod(
        formValues?.[Fields.leasingPeriod],
        formValues?.[Fields.paymentFrequency],
      ),
    },
    {
      id: 'leasingPaymentType',
      label: t('common.leasingOption'),
      value: formatMappedValue(
        formValues?.[Fields.leasingPayment],
        leasingPaymentLabels,
      ),
    },
    {
      id: 'paymentFrequency',
      label: t('common.paymentFrequency'),
      value: formatMappedValue(
        formValues?.[Fields.paymentFrequency],
        paymentFrequencyLabels,
      ),
    },
    {
      id: 'endDate',
      label: t('common.expiryDate'),
      value: endDate,
    },
    {
      id: 'expectedKmPerYear',
      label: t('common.expectedKmPerYear'),
      value: isPassengerCar
        ? formValues?.[Fields.expectedKmPerYear] || '-'
        : '-',
    },
    {
      id: 'carAttachment',
      label: t('common.attachment'),
      value: isPassengerCar
        ? formatFileName(formValues?.[Fields.carAttachment])
        : '-',
    },
    {
      id: 'description',
      label: t('common.description'),
      value: formValues?.[Fields.description] || '-',
    },
    {
      id: 'extendedDescription',
      label: t('common.extendedDescription.optional'),
      value: formValues?.[Fields.extendedDescription] || '-',
    },
  ];

  const rightColumn: SummaryItem[] = [
    {
      id: 'municipalOrRegionalPurpose',
      label: t('common.municipalOrRegionalPurpose'),
      value: formValues?.[Fields.municipalOrRegionalPurpose] || '-',
    },
    {
      id: 'lessee',
      label: t('select.lessee'),
      value: formatSelectedOrNewLessee(
        formValues?.[Fields.lessee],
        formValues?.[Fields.newLessee],
        lesseeOptions,
      ),
    },
    {
      id: 'eanNumber',
      label: t('common.EANnumber'),
      value: formatSelectedOrNewEanNumber(
        formValues?.[Fields.eanNumber],
        formValues?.[Fields.newEanNumber],
      ),
    },
    {
      id: 'deliveryApprover',
      label: t('common.deliveryApproverContact'),
      value: formatSelectedOrNewContact(
        formValues?.[Fields.deliveryApprover],
        formValues?.[Fields.newDeliveryApprover],
        contactOptions,
      ),
    },
    {
      id: 'contactPerson',
      label: t('select.contactPerson'),
      value: formatSelectedOrNewContact(
        formValues?.[Fields.contactPerson],
        formValues?.[Fields.newContactPerson],
        contactOptions,
      ),
    },
    {
      id: 'authorizedSignatory',
      label: t('select.authorizedSignatory'),
      value: formatAuthorizedSignatories(
        formValues?.[Fields.authorizedSignatory],
        formValues?.[Fields.newAuthorizedSignatory],
        contactOptions,
      ),
    },
    {
      id: 'createMultipleIdenticalAgreements',
      label: t('common.createMultipleIdenticalAgreements'),
      value: formValues?.[Fields.createMultipleIdenticalAgreements]
        ? `${t('common.yes', 'Ja')} (${formValues[Fields.numberOfIdenticalAgreements] ?? 0})`
        : formatBoolean(
            formValues?.[Fields.createMultipleIdenticalAgreements],
            t('common.yes', 'Ja'),
            t('common.no', 'Nej'),
          ),
    },
    {
      id: 'remarks',
      label: t('common.remarks.optional'),
      value: formValues?.[Fields.remarks] || '-',
    },
    {
      id: 'attachment',
      label: t('common.attachment.optional'),
      value: formatFileName(formValues?.[Fields.attachment]),
    },
    // {
    //   label: t("checkbox.confirmMayor", { municipality }),
    //   value: formatBoolean(
    //     formValues?.[Fields.confirmMayor],
    //     t("common.yes", "Ja"),
    //     t("common.no", "Nej"),
    //   ),
    // },
    // {
    //   label: t("checkbox.confirmMunicipalDirector", { municipality }),
    //   value: formatBoolean(
    //     formValues?.[Fields.confirmMunicipalDirector],
    //     t("common.yes", "Ja"),
    //     t("common.no", "Nej"),
    //   ),
    // },
  ];

  return (
    <div className="mx-auto my-8 w-full max-w-230 px-4 md:mt-10">
      <section className="rounded-[20px] border border-neutral-10 bg-white p-8 shadow-md-8 md:px-10 md:py-9">
        <header>
          <h1 className="text-[22px] font-semibold leading-8 text-neutral-100">
            {t('summary.title')}
          </h1>
        </header>

        <div className="mt-8 border-t border-neutral-10 pt-8">
          <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
            <dl className="space-y-4">
              {leftColumn.map((item) => (
                <SummaryField
                  key={item.id}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </dl>

            <dl className="space-y-4">
              {rightColumn.map((item) => (
                <SummaryField
                  key={item.id}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </dl>
          </div>
        </div>

        <footer className="mt-12 border-t border-neutral-10 pt-8">
          <div className="flex justify-end gap-4">
            <button
              type="button"
              className="rounded-md px-4 py-2 text-sm font-semibold text-neutral-100 hover:bg-neutral-5 focus:outline-hidden focus:ring-2 focus:ring-blue-20"
              onClick={() =>
                navigate('/new-agreement', {
                  state: navigationState,
                })
              }
            >
              {t('button.back')}
            </button>

            <button
              type="button"
              className="rounded-md bg-blue-110 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-100 focus:outline-hidden focus:ring-2 focus:ring-blue-20 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isCreatingNewAgreement || !navigationState}
              aria-busy={isCreatingNewAgreement}
              onClick={async () => {
                if (!navigationState || isCreatingNewAgreement) return;

                let submitAttempted = false;

                try {
                  const basePayload = toNewAgreementApplicationPayload(
                    navigationState as NewAgreementFormValues,
                  );

                  const [carAttachment, attachment] = await Promise.all([
                    serializeFiles(navigationState[Fields.carAttachment]),
                    serializeFiles(navigationState[Fields.attachment]),
                  ]);

                  const payload = {
                    ...basePayload,
                    [Fields.fixedFirstPayment]:
                      basePayload[Fields.fixedFirstPayment].trim() || '0',
                    [Fields.expectedKmPerYear]:
                      Number.parseInt(
                        String(basePayload[Fields.expectedKmPerYear]).trim(),
                        10,
                      ) || 0,
                    [Fields.numberOfIdenticalAgreements]: toIntegerOrZero(
                      basePayload[Fields.numberOfIdenticalAgreements],
                    ),
                    [Fields.carAttachment]: carAttachment,
                    [Fields.attachment]: attachment,
                    leasingPaymentAmount:
                      navigationState.leasingPaymentAmount ?? null,
                  };

                  const request = createNewAgreementRequest(
                    navigationState,
                    payload,
                  );

                  submitAttempted = true;
                  const agreementId = await createNewAgreement(request);

                  navigate(
                    `/new-agreement/receipt/${encodeURIComponent(agreementId)}`,
                    {
                      replace: true,
                      state: { agreementId },
                    },
                  );
                } catch (error) {
                  console.error('Create new agreement failed:', error);

                  if (!submitAttempted) {
                    showToast({
                      type: 'danger',
                      headline: t('common.newAgreementSave.onError'),
                      message: t('common.onError.message'),
                      size: 'long',
                      link: { href: 'toast' },
                    });
                  }
                }
              }}
            >
              {isCreatingNewAgreement
                ? t('button.sendingApplication', 'Sender...')
                : t('button.sendApplication')}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
